import type { SectionInstance, Theme, ThemeSettings } from "@dk/theme"
import type { PaymentMethodPublic } from "../lib/api"
import { defineStore } from "pinia"
import { devStoreHandle } from "../lib/devstore"
import { previewToken, stripPreviewParam } from "../lib/previewFlag"
import { experimentDoc } from "../lib/experiment"
import type { PublicExperiment } from "../lib/experiment"

/** Store bootstrap: read from localStorage synchronously (render-before-network),
 *  then revalidated against GET /api/storefront/bootstrap. Carries the full theme
 *  doc (settings + layout + page sections) + menus. In builder preview mode a
 *  `previewDoc` override replaces the published theme live over postMessage. */

export interface MenuItem {
  label: string
  url: string
  children?: MenuItem[]
  /** mega-menu feature image (top-level items) — turns the dropdown into a mega panel */
  image?: string
  /** where the feature image links (defaults to the item's own url) */
  image_url?: string
  /** caption under the feature image (defaults to the item label) */
  image_label?: string
}

export interface StoreAddress {
  line1?: string
  line2?: string
  city?: string
  province?: string
  zip?: string
  country?: string
}
export interface StoreInfo {
  name: string
  handle: string
  currency: string
  locale?: string
  timezone?: string
  contact_email?: string
  contact_phone?: string
  address?: StoreAddress
}

// theme.settings is the full published theme doc: { schema_version, settings,
// layout, pages }. Colour/typography tokens live one level in, at .settings.
interface BootstrapPayload {
  store?: StoreInfo
  theme?: { version: number; settings: Theme }
  menus?: Record<string, MenuItem[]>
  // store_settings namespace/key/value (scripts, policies, …)
  settings?: Record<string, Record<string, unknown>>
  /**
   * The store's enabled gateways, public config only, host-aware — the same shape
   * GET /payment-methods returns.
   *
   * It rides this payload so a page can decide whether a wallet button is worth
   * mounting without a `no-store` round trip: /payment-methods is right for the
   * checkout, which asks once, and wrong for the product page, which would ask on
   * every view. The CHECKOUT deliberately still calls the endpoint — it needs the
   * answer at the moment the buyer is paying, not the one cached with the theme.
   */
  payments?: PaymentMethodPublic[]
  /** Running A/B tests — every arm's patch; the browser picks its own (D2). */
  experiments?: PublicExperiment[]
  version?: number
}

const emptyTheme: Theme = {
  schema_version: 2,
  settings: { colors: {}, typography: {} },
  layout: { header: [], footer: [] },
  pages: {
    home: { sections: [] },
    product: { templates: { default: [] } },
    collection: { templates: { default: [] } },
    page: { sections: [] },
    cart: { sections: [] },
  },
}

/**
 * Host-scoped, because one browser can hold two stores of this platform.
 *
 * A function and not a module-level const: read at import time it makes this module
 * unimportable anywhere without a DOM, and this store is reached from components
 * that node tests render (three test files already stub `globalThis.location` for
 * exactly this, and adding a wallet strip to the buy-buttons block would have made it
 * five). Reading it per call costs nothing measurable and takes the workaround away.
 */
const cacheKey = () => `dk:bootstrap:${location.host}`

/** One bootstrap fetch per store instance. main.ts starts it before mounting a cold
 *  visit and App.vue awaits it on mount; both must get the same request. Keyed by
 *  the store so each test's fresh Pinia still fetches. */
const loads = new WeakMap<object, Promise<void>>()

export const useBootstrapStore = defineStore("bootstrap", {
  state: () => ({
    payload: readCache(),
    loaded: false,
    previewDoc: null as Theme | null, // builder live-preview override
    /** Why there is no storefront to render, or "" when there is one. The two
     *  reasons need different words and land on different people: "closed" is
     *  the seller's own switch and is aimed at a shopper; "unconnected" means
     *  this host reaches us but is not wired to any store, which only the person
     *  who pointed the DNS can fix. Collapsing them told a seller whose domain
     *  was half-connected that their shop was closed, and sent them to the one
     *  setting that was not the problem. App.vue renders the screen and nothing
     *  else. */
    unavailable: "" as "" | "closed" | "unconnected",
  }),
  getters: {
    themeDoc(state): Theme {
      // The builder's live document is shown as the seller made it; the published
      // one gets this session's A/B arm applied before anything renders from it.
      if (state.previewDoc) return state.previewDoc
      const base = state.payload?.theme?.settings
      return base ? experimentDoc(base, state.payload?.experiments) : emptyTheme
    },
    themeSettings(): ThemeSettings {
      return this.themeDoc.settings ?? { colors: {}, typography: {} }
    },
    homeSections(): SectionInstance[] {
      return this.themeDoc.pages?.home?.sections ?? []
    },
    // Product/collection template sections (the "default" template). Rendered
    // by ProductPage/CollectionPage around their core content; empty = the
    // built-in layout is used unchanged.
    productSections(): SectionInstance[] {
      return this.themeDoc.pages?.product?.templates?.default ?? []
    },
    collectionSections(): SectionInstance[] {
      return this.themeDoc.pages?.collection?.templates?.default ?? []
    },
    /** Standard-page template sections. 56 of the 65 section defs declare
     *  `pages: ["page"]`, but nothing rendered them until now — anything a
     *  seller configured here vanished silently. Every shipped template has
     *  `pages.page.sections == []`, so wiring this is a no-op on all of them. */
    pageSections(): SectionInstance[] {
      return this.themeDoc.pages?.page?.sections ?? []
    },
    /** Whether a bootstrap payload exists yet (cached or fetched). Lets pages
     *  tell "store genuinely has no sections" (show DefaultHome) apart from
     *  "haven't loaded yet" (show a skeleton, not a speculative DefaultHome). */
    hasPayload(state): boolean {
      return state.payload != null
    },
    storeName(state): string {
      return state.payload?.store?.name ?? ""
    },
    store(state): StoreInfo | null {
      return state.payload?.store ?? null
    },
    mainMenu(state): MenuItem[] {
      return state.payload?.menus?.["main-menu"] ?? []
    },
    /** Footer link columns — menu handle comes from theme footer settings. */
    footerMenu(state): MenuItem[] {
      const handle = this.themeSettings.footer?.menu || "footer"
      return state.payload?.menus?.[handle] ?? []
    },
    /** Any menu by handle — the footer's `menu` blocks each pick their own. */
    menuByHandle(state) {
      return (handle: string): MenuItem[] => state.payload?.menus?.[handle] ?? []
    },
    /**
     * Gateways this store has enabled, as the server described them for THIS host.
     *
     * Empty is a real answer and the common one on a dev store: it means no gateway
     * is connected, so nothing that needs one should render.
     */
    payments(state): PaymentMethodPublic[] {
      return state.payload?.payments ?? []
    },
    /** store_settings namespace map (scripts, policies, …). */
    storeSettings(state): Record<string, Record<string, unknown>> {
      return state.payload?.settings ?? {}
    },
    /**
     * The store's own country (ISO 3166-1 alpha-2), when the seller has set an
     * address. Used as the checkout's default destination so a Vietnamese shop does
     * not open its address form on the United States.
     */
    storeCountry(state): string {
      return state.payload?.store?.address?.country ?? ""
    },
  },
  actions: {
    load(): Promise<void> {
      let p = loads.get(this)
      if (!p) {
        p = this.fetchPayload()
        loads.set(this, p)
      }
      return p
    },
    async fetchPayload() {
      try {
        // A `?dk_preview={token}` in the page URL previews a specific unpublished
        // theme's draft (marketplace / library preview). Token "1" is the builder
        // iframe's postMessage mode and carries no token. Preview payloads are
        // never cached (they must not replace the store's real theme).
        //
        // Read from previewFlag, which captured it at module load — the address
        // bar no longer has it by the time anything else looks, because the token
        // is a bearer grant and leaving it on a public {key}.dikafy.com URL means
        // every copied link hands it on.
        const token = previewToken()
        const isTokenPreview = !!token
        const url = isTokenPreview
          ? `/api/storefront/bootstrap?dk_preview=${encodeURIComponent(token)}`
          : "/api/storefront/bootstrap"
        // Spent: the request that needs it has been built. Done before the await
        // so the URL is clean on the first frame the shopper sees, rather than
        // flickering the token away when the network answers.
        stripPreviewParam()
        // no-cache = always revalidate via ETag (fast 304s) so a publish is
        // reflected on the next load, while the CDN still honours s-maxage.
        // Dev-only: forward the previewed store so the shared localhost proxy
        // resolves it (no-op / dead code in production builds).
        const devStore = devStoreHandle()
        const res = await fetch(url, {
          cache: "no-cache",
          headers: devStore ? { "X-Store-Handle": devStore } : undefined,
        })
        if (res.ok) {
          this.payload = (await res.json()) as BootstrapPayload
          this.unavailable = ""
          if (!isTokenPreview) localStorage.setItem(cacheKey(), JSON.stringify(this.payload))
        } else if (res.status === 503 || res.status === 404) {
          // 503 + code "store_unavailable": the seller (or the platform) switched
          // this storefront off. 404: nothing in the domains table resolves this
          // host to a store — a custom domain whose DNS already arrives here while
          // its row is still pending, or one nobody ever connected.
          //
          // The cache MUST be dropped here. This store renders synchronously from
          // localStorage before the network answers, so a returning visitor would
          // otherwise keep browsing a fully-rendered shop that is closed, and add
          // to a cart whose every API call 503s.
          //
          // Deliberately NOT done for other non-OK statuses: a transient 500 or a
          // gateway blip should keep the cached storefront on screen, which is the
          // behaviour this branch is carved out of.
          this.unavailable = res.status === 404 ? "unconnected" : "closed"
          this.payload = null
          try {
            localStorage.removeItem(cacheKey())
          } catch {
            // private mode / quota — nothing to clean up
          }
        }
      } catch {
        // offline / backend not up: cached payload (if any) keeps rendering
      } finally {
        this.loaded = true
      }
    },
    /** Builder preview: replace the theme doc live (no network, no publish). */
    setPreviewDoc(doc: Theme | null) {
      this.previewDoc = doc
    },
    /** Builder preview: run a mutator over the live preview document.
     *
     *  The narrow half of the preview channel — see `@dk/theme`'s `patch.ts` for
     *  why a settings keystroke does not deserve a full document round trip. The
     *  mutator is passed in rather than named here so the patch helpers stay in
     *  the lazily-loaded `lib/preview.ts` and out of every shopper's entry chunk;
     *  this store is entry code.
     *
     *  False means the patch found no target, which the caller answers with a
     *  full replace: the two documents have diverged, and a patch that silently
     *  does nothing would leave the preview showing something the seller is no
     *  longer editing. */
    mutatePreviewDoc(fn: (doc: Theme) => boolean): boolean {
      return this.previewDoc ? fn(this.previewDoc) : false
    },
  },
})

function readCache(): BootstrapPayload | null {
  try {
    const raw = localStorage.getItem(cacheKey())
    return raw ? (JSON.parse(raw) as BootstrapPayload) : null
  } catch {
    return null
  }
}
